<template>
	<div class="splash-story">
		<div ref="splashStoryContent" class="splash-story__content">
			<!-- Use img tag instead of CSS background for proper cache usage -->
			<img
				v-if="actualRouteForImage"
				:key="actualRouteForImage"
				:src="actualRouteForImage"
				class="splash-story__background"
				alt=""
				loading="eager"
			/>
			<div class="skip-container-all">
				<LabeledDecorBox
					:text="translate('button.skip')"
					icon="skip-all"
					@click.stop="handleStorySkipAll"
				/>
			</div>
			<p class="splash-story__paragraph">
				<span ref="paragraphRef"></span>
			</p>
			<div class="skip-container">
				<div class="skip">
					<span>{{ translate("ui.next") }}</span>
					<div class="icon-skip" />
				</div>
			</div>
		</div>
		<div
			v-show="shouldShowOverlay"
			class="skip-overlay"
			@click.stop="handleStorySkip"
		/>
	</div>
</template>

<script lang="ts" setup>
import LabeledDecorBox from "@/components/SVG/LabeledDecorBox.vue";
import { PLAGUE_ENABLED } from "@/config/constants.ts";
import type {
	FX1AudioNames,
	UIAudioNames,
} from "@/game/entities/config/audio/audioConfig";
import {
	AudioSpriteNames,
	CutscenesAudioNames,
} from "@/game/entities/config/audio/audioConfig";
import SoundService from "@/game/SoundService.ts";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker.ts";
import router from "@/router";
import useRuntimeStore from "@/store/useRuntimeStore.ts";
import { RouteNames } from "@/types/ui.ts";
import { getImageUrl } from "@/utils/preload";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	onMounted,
	onUnmounted,
	ref,
	useTemplateRef,
	watch,
} from "vue";
import { useRoute } from "vue-router";
import { storyParagraphs } from "./StoryConfig.ts";

// Constants
const FADE_IN_OUT_DURATION = 1_000; // milliseconds
const FADE_IN_DELAY = 250; // milliseconds
const DRAMATIC_PAUSE_DURATION = 1_000; // milliseconds
const OVERLAY_SHOW_DELAY = 500; // milliseconds
const STEP_11_DELAY = 2_000; // milliseconds
const STEP_11_FAST_DELAY = 250; // milliseconds
const FADE_OUT_TRANSITION_DURATION = 0; // milliseconds

// Chapter constants
const CHAPTER_1 = 1;
const CHAPTER_2 = 2;
const CHAPTER_3 = 3;
const CHAPTER_4 = 4;
const DRAMATIC_PAUSE_STEP = 10;

// Chapter audio mapping
const CHAPTER_AUDIO_METHODS = {
	[CHAPTER_1]: {
		play: (step: CutscenesAudioNames): void =>
			soundService.playCutsceneIntroAudio(step),
		stop: (): void =>
			soundService.stopSpriteAudio(AudioSpriteNames.CUTSCENES_INTRO),
	},
	[CHAPTER_2]: {
		play: (step: CutscenesAudioNames): void =>
			soundService.playCutscenetOutroAudioChapter1(step),
		stop: (): void =>
			soundService.stopSpriteAudio(
				AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_1,
			),
	},
	[CHAPTER_3]: {
		play: (step: CutscenesAudioNames): void =>
			soundService.playCutscenetOutroAudioChapter2(step),
		stop: (): void =>
			soundService.stopSpriteAudio(
				AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_2,
			),
	},
	[CHAPTER_4]: {
		play: (step: CutscenesAudioNames): void =>
			soundService.playCutscenetOutroAudioChapter3(step),
		stop: (): void =>
			soundService.stopSpriteAudio(
				AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_3,
			),
	},
} as const;

const route = useRoute();

const ticker = markRaw(useGameTicker());
const emitter = markRaw(useEmitter());
const soundService = markRaw(SoundService.getInstance());

const runtimeStore = useRuntimeStore();
const { firstClick } = storeToRefs(runtimeStore);

const actualRouteForImage = ref<string>("");
const shouldShowOverlay = ref<boolean>(false);
const splashStoryContent = useTemplateRef<HTMLElement>("splashStoryContent");

// Array to hold preloaded images for current chapter
const preloadedImages = ref<Map<number, string>>(new Map());

const currentStoryId = computed((): number => {
	return +route.params.id;
});

const currentChapter = computed((): number => {
	return +route.params.chapter || 1;
});

const currentParagraphText = computed((): string => {
	return storyParagraphs[currentChapter.value][currentStoryId.value] || "";
});

const paragraphRef = ref<HTMLElement | null>(null);

// NOTE: prevent user from interacting with the story while it's paused
const isInteractionLocked = ref<boolean>(false);

/**
 * Handles the main story skip action for the current step
 * Determines the appropriate action based on current chapter and interaction state
 */
const handleStorySkip = (): void => {
	if (isInteractionLocked.value || !firstClick.value) return;

	soundService.playGenerickClick();
	fadeOutText(paragraphRef.value as HTMLElement);

	if (currentChapter.value === CHAPTER_1) {
		handleChapter1Skip();
	} else if (
		currentChapter.value === CHAPTER_2 ||
		currentChapter.value === CHAPTER_3 ||
		currentChapter.value === CHAPTER_4
	) {
		handleChapterOutroSkip();
	}
};

/**
 * Handles skipping all remaining story content and navigating to the main game
 */
const handleStorySkipAll = (): void => {
	if (isInteractionLocked.value) return;

	soundService.playGenerickClick();
	fadeOutText(paragraphRef.value as HTMLElement);

	// NOTE: skip all story paragraphs and go to the layout view
	stopChapterAudio();
	completeStoryAndNavigateToLayout();
};

/**
 * Navigates to the next story step within the current chapter
 */
const jumpToNextStoryStep = (): void => {
	router.push({
		name: RouteNames.STORY,
		params: { chapter: currentChapter.value, id: currentStoryId.value + 1 },
	});
};

/**
 * Calculates the audio step identifier for the current story ID
 * @returns The cutscene audio name for the current step
 */
const calculateCutsceneAudioStep = (): CutscenesAudioNames => {
	const key =
		`STEP_${currentStoryId.value}` as keyof typeof CutscenesAudioNames;
	return CutscenesAudioNames[key];
};

/**
 * Plays the appropriate audio for the current chapter and step
 */
const playChapterAudio = (): void => {
	const audioMethods =
		CHAPTER_AUDIO_METHODS[
			currentChapter.value as keyof typeof CHAPTER_AUDIO_METHODS
		];
	if (audioMethods) {
		audioMethods.play(calculateCutsceneAudioStep());
	}
};

/**
 * Stops the appropriate audio for the current chapter
 */
const stopChapterAudio = (): void => {
	const audioMethods =
		CHAPTER_AUDIO_METHODS[
			currentChapter.value as keyof typeof CHAPTER_AUDIO_METHODS
		];
	if (audioMethods) {
		audioMethods.stop();
	}
};

/**
 * Completes the current story and navigates to the main layout
 */
const completeStoryAndNavigateToLayout = (): void => {
	if (!ticker.isLocked) ticker.start();
	stopChapterAudio();
	soundService.stopIntroMusic();
	soundService.playMainMusic();
	soundService.playCityAmbient();
	emitter.emit(EmitterEvents.PhaserAnimateIntroCamera);
	router.push({ name: RouteNames.LAYOUT_VIEW });
};

/**
 * Handles the dramatic pause between story steps 10 and 11
 */
const handleDramaticPause = (): void => {
	isInteractionLocked.value = true;
	splashStoryContent.value?.classList.add("splash-story__content--fade-out");
	setTimeout((): void => {
		jumpToNextStoryStep();
	}, DRAMATIC_PAUSE_DURATION);
};

/**
 * Handles story progression for chapter 1
 */
const handleChapter1Skip = (): void => {
	if (!storyParagraphs[currentChapter.value][currentStoryId.value + 1]) {
		completeStoryAndNavigateToLayout();
	} else {
		stopChapterAudio();
		if (currentStoryId.value === DRAMATIC_PAUSE_STEP) {
			handleDramaticPause();
		} else {
			jumpToNextStoryStep();
		}
	}
};

/**
 * Handles story progression for chapter 2 and 3
 */
const handleChapterOutroSkip = (): void => {
	if (!storyParagraphs[currentChapter.value][currentStoryId.value + 1]) {
		completeStoryAndNavigateToLayout();
	} else {
		stopChapterAudio();
		jumpToNextStoryStep();
	}
};

/**
 * Fades in text content with a smooth transition
 * @param element - The HTML element to apply the fade-in effect to
 * @param text - The text content to display
 */
const fadeInText = (element: HTMLElement, text: string): void => {
	element.style.opacity = "0";
	element.innerHTML = text;
	setTimeout((): void => {
		element.style.transition = `opacity ${FADE_IN_OUT_DURATION}ms ease-in-out`;
		element.style.opacity = "1";
	}, FADE_IN_DELAY);
};

/**
 * Instantly fades out text content
 * @param element - The HTML element to apply the fade-out effect to
 */
const fadeOutText = (element: HTMLElement): void => {
	element.style.transition = `opacity ${FADE_OUT_TRANSITION_DURATION}ms ease-in-out`;
	element.style.opacity = "0";
};

/**
 * Initializes and displays the story text for the current step
 * Handles text translation and formatting (bold tags)
 */
const initNewStoryText = (): void => {
	if (paragraphRef.value && currentParagraphText.value) {
		let paragraphTranslated = translate(currentParagraphText.value);
		paragraphTranslated = paragraphTranslated.replace("{b}", "<strong>");
		paragraphTranslated = paragraphTranslated.replace("{/b}", "</strong>");
		fadeInText(paragraphRef.value, paragraphTranslated);
	}
};

/**
 * Updates the background image path for the current chapter and story step
 * Uses getImageUrl to ensure same URL construction as preload
 */
const updateImagePath = (): void => {
	const imageUrl = preloadedImages.value.get(currentStoryId.value);
	if (imageUrl) {
		actualRouteForImage.value = imageUrl;
	} else {
		// Fallback to dynamic loading if not in preloaded array
		const plagueFolder =
			PLAGUE_ENABLED && currentChapter.value === 1 ? "plague/" : "";
		actualRouteForImage.value = getImageUrl(
			`/story/chapters/${currentChapter.value}/${plagueFolder}step-${currentStoryId.value}.avif`,
		);
	}
};

/**
 * Preloads all images for the current chapter into an array
 */
const preloadChapterImages = async (): Promise<void> => {
	const chapterSteps = storyParagraphs[currentChapter.value];
	if (!chapterSteps) return;

	const stepIds = Object.keys(chapterSteps).map(
		(key: string): number => +key,
	);

	for (const stepId of stepIds) {
		const plagueFolder =
			PLAGUE_ENABLED && currentChapter.value === 1 ? "plague/" : "";
		const imageUrl = getImageUrl(
			`/story/chapters/${currentChapter.value}/${plagueFolder}step-${stepId}.avif`,
		);

		// Create and load image
		const img = new Image();
		img.src = imageUrl;

		await new Promise<void>((resolve: () => void): void => {
			img.onload = (): void => {
				preloadedImages.value.set(stepId, imageUrl);
				// console.error(
				// 	`[STORY] Preloaded image for step ${stepId}: ${imageUrl}`,
				// );
				resolve();
			};
			img.onerror = (): void => {
				// console.error(
				// 	`[STORY] Failed to preload image for step ${stepId}: ${imageUrl}`,
				// );
				resolve(); // Continue even if one image fails
			};
		});
	}

	// console.error(
	// 	`[STORY] Preloaded ${preloadedImages.value.size} images for chapter ${currentChapter.value}`,
	// );
};

/**
 * Handles audio finished events from Phaser
 * @param audioName - The name of the audio that finished playing
 */
const onPhaserAudioFinished = (
	audioName: UIAudioNames | CutscenesAudioNames | FX1AudioNames,
): void => {
	if (audioName === calculateCutsceneAudioStep()) {
		handleStorySkip();
	}
};

/**
 * Handles the special fade-in sequence for step 11 and plays chapter audio
 * @param delay - The delay before enabling interaction and playing audio
 */
const handleStep11Sequence = (delay: number): void => {
	splashStoryContent.value?.classList.replace(
		"splash-story__content--fade-out",
		"splash-story__content--fade-in",
	);
	setTimeout((): void => {
		playChapterAudio();
		// Enable story skip option after the intro cutscene
		isInteractionLocked.value = false;
	}, delay);
};

/**
 * Initializes audio and visual elements for the current story step
 */
const initializeStoryStep = (): void => {
	if (currentStoryId.value === 11) {
		handleStep11Sequence(STEP_11_DELAY);
	} else {
		playChapterAudio();
	}
};

onMounted((): void => {
	initNewStoryText();
	ticker.stop();

	// Preload all chapter images first
	preloadChapterImages().then((): void => {
		updateImagePath(); // Set initial image after preloading
	});

	soundService.stopMainMusic();
	soundService.playIntroMusic();

	initializeStoryStep();

	setTimeout((): void => {
		shouldShowOverlay.value = true;
	}, OVERLAY_SHOW_DELAY);

	emitter.on(EmitterEvents.PhaserAudioFinished, onPhaserAudioFinished);
});

onUnmounted((): void => {
	emitter.off(EmitterEvents.PhaserAudioFinished, onPhaserAudioFinished);
});

watch(
	(): string | Array<string> => route.params.id,
	(): void => {
		// NOTE: reset story skip button if ID changes
		initNewStoryText();
		updateImagePath(); // Use preloaded image

		if (currentStoryId.value === 11 && currentChapter.value === 1) {
			handleStep11Sequence(STEP_11_FAST_DELAY);
		} else {
			playChapterAudio();
		}
	},
);

watch(
	(): number => currentChapter.value,
	(): void => {
		// Preload images when chapter changes
		preloadChapterImages().then((): void => {
			updateImagePath();
		});
	},
);

// Debug: Watch for image URL changes
// watch(actualRouteForImage, (newUrl, oldUrl) => {
// 	console.error(
// 		`[STORY_VIEW] Image URL changed from "${oldUrl}" to "${newUrl}"`,
// 	);
// });
</script>

<style lang="scss" scoped>
.splash-story {
	position: absolute;
	z-index: 999;
	background: rgb(0, 0, 0);
	width: 100%;
	height: 100%;
	@include flex-row-center;

	&__content {
		position: absolute;
		@include flex-col-end;
		align-items: flex-end;
		gap: 1.875rem;
		z-index: 1;
		padding: 0 1rem;
		padding-bottom: 1.25rem;
		width: 100%;
		max-width: 27rem;
		height: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			z-index: 1;
			background-image: linear-gradient(
				to bottom,
				transparent 0%,
				transparent 60%,
				rgba(0, 0, 0, 0.6) 70%,
				rgba(0, 0, 0, 1) 90%,
				rgba(0, 0, 0, 1) 100%
			);
			width: 100%;
			height: 100%;
			content: "";
		}

		& > * {
			z-index: 1;
		}

		&--fade-in {
			animation: fade-in 1s ease-in-out forwards;
		}

		&--fade-out {
			animation: fade-out 1s ease-in-out forwards;
		}

		@keyframes fade-in {
			0% {
				opacity: 0;
			}
			100% {
				opacity: 1;
			}
		}

		@keyframes fade-out {
			0% {
				opacity: 1;
			}
			100% {
				opacity: 0;
			}
		}
	}

	&__paragraph {
		position: relative;
		z-index: 2; /* Above background image */
		padding-bottom: 0.25rem;
		padding: 0;
		width: 100%;
		overflow: hidden;
		color: #fff3dd;
		font-size: 1.25rem;
		text-align: center;
	}

	.skip-container-all {
		@include flex-col-center;
		position: absolute;
		top: 0;
		right: 0;
		align-items: flex-end;
		z-index: 2; /* Above background image */
		transition: all 0.3s ease-in-out;
		padding: calc(var(--safe-area-inset-top) + 0.125rem) 0.5rem 0;
		width: 100%;
	}

	.skip-container {
		@include flex-row-center;
		align-items: flex-end;
		z-index: 2; /* Above background image */
		transition: all 0.3s ease-in-out;

		.skip {
			@include flex-row-center;
			gap: 0.5rem;
			padding: 0.5rem 1rem;

			span {
				padding-bottom: 0.25rem;
				color: #f4d278;
				font-size: 1rem;
				line-height: 1rem;
				text-transform: uppercase;
			}

			.icon-skip {
				width: 0.813rem;
				height: 0.75rem;
			}
		}
	}

	:deep(.ragged-box-bg) {
		position: relative;
		top: 0;
		left: 0;
		width: 100%;
	}

	:deep(.progress-bar) {
		flex-shrink: 0;
		margin-top: 0.3rem;
	}

	:deep(.icon-check) {
		margin-top: 0.25rem;
		margin-left: 0.25rem;
		width: 0.75rem;
		height: 0.75rem;
	}

	.splash-story__background {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	.skip-overlay {
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 1;
		width: 100%;
		height: 66%;
	}
}
</style>
